import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  InputAdornment,
  TablePagination,
  Select,
  MenuItem,
  Card,
  CardContent,
  Grid,
  Rating,
  Tooltip,
  IconButton,
  Collapse,
  Button,
} from '@mui/material';
import {
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Star as StarIcon,
  Download as DownloadIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { AuthContext } from '../contexts/AuthContext';
import feedbackService from '../services/feedbackService';
import { PageTitleContainer } from '../components/common';

// Stat Card Component
const StatCard = ({ title, value, subtitle, color = 'primary' }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {title}
      </Typography>
      <Typography variant="h4" component="div" color={`${color}.main`}>
        {value}
      </Typography>
      {subtitle && (
        <Typography variant="body2" color="text.secondary">
          {subtitle}
        </Typography>
      )}
    </CardContent>
  </Card>
);

// Expandable Row Component
const FeedbackRow = ({ feedback, expanded, onToggle }) => {
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return isNaN(date.getTime())
      ? 'Invalid Date'
      : date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
  };

  return (
    <>
      <TableRow
        sx={{
          '& > *': { borderBottom: 'unset' },
          cursor: 'pointer',
          '&:hover': { backgroundColor: 'rgba(0,0,0,0.03)' },
          '&:nth-of-type(even)': { backgroundColor: 'white !important' }
        }}
        onClick={onToggle}
      >
        <TableCell>
          <IconButton size="small">
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {feedback.user_name || 'N/A'}
            </Typography>
            {feedback.user_email && (
              <Typography variant="caption" color="text.secondary">
                {feedback.user_email}
              </Typography>
            )}
          </Box>
        </TableCell>
        <TableCell>
          <Rating value={feedback.ailyce_rating} readOnly size="small" />
        </TableCell>
        <TableCell>
          {feedback.is_enthusiastic ? (
            <Chip
              icon={<CheckCircleIcon />}
              label="Yes"
              color="success"
              size="small"
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          ) : (
            <Chip
              icon={<CancelIcon />}
              label="No"
              color="error"
              size="small"
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          )}
        </TableCell>
        <TableCell>
          {feedback.needs_training ? (
            <Chip label="Yes" color="warning" size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
          ) : (
            <Chip label="No" color="default" size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
          )}
        </TableCell>
        <TableCell>
          <Rating value={feedback.help_rating} readOnly size="small" />
        </TableCell>
        <TableCell>
          <Rating value={feedback.food_quality_rating} readOnly size="small" />
        </TableCell>
        <TableCell>
          <Typography variant="body2" color="text.secondary">
            {formatDate(feedback.created_at)}
          </Typography>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              <Typography variant="h6" gutterBottom component="div">
                Detailed Feedback
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      1. Ailyce Feedback
                    </Typography>
                    <Typography variant="body2">
                      {feedback.ailyce_feedback || 'No feedback provided'}
                    </Typography>
                  </Paper>
                </Grid>
                {!feedback.is_enthusiastic && feedback.not_enthusiastic_reason && (
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2, bgcolor: 'error.50' }}>
                      <Typography variant="subtitle2" color="error" gutterBottom>
                        2. Reason Not Enthusiastic
                      </Typography>
                      <Typography variant="body2">
                        {feedback.not_enthusiastic_reason}
                      </Typography>
                    </Paper>
                  </Grid>
                )}
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      5. Food Quality Remarks
                    </Typography>
                    <Typography variant="body2">
                      {feedback.food_quality_remarks || 'No remarks provided'}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      6. Suggestions
                    </Typography>
                    <Typography variant="body2">
                      {feedback.suggestions || 'No suggestions provided'}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      7. Foundation Remarks
                    </Typography>
                    <Typography variant="body2">
                      {feedback.foundation_remarks || 'No remarks provided'}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const FeedbackManagement = () => {
  const { user } = useContext(AuthContext);
  const [feedbacks, setFeedbacks] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [expandedRow, setExpandedRow] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportResult, setExportResult] = useState(null);

  // Fetch feedback on component mount
  useEffect(() => {
    fetchFeedback();
  }, []);

  const fetchFeedback = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await feedbackService.getAllFeedback();
      setFeedbacks(response.data || []);
      setStats(response.stats || null);
    } catch (err) {
      const errorMessage =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        err.message ||
        'Failed to fetch feedback';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRowToggle = (feedbackId) => {
    setExpandedRow(expandedRow === feedbackId ? null : feedbackId);
  };

  const handleExportToExcel = async () => {
    try {
      setExporting(true);
      setExportResult(null);
      await feedbackService.exportFeedbackToExcel();
      setExportResult({ success: true, message: 'Feedback exported successfully!' });
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to export feedback';
      setExportResult({ success: false, message: errorMsg });
    } finally {
      setExporting(false);
    }
  };

  // Filter feedbacks based on search term
  const filteredFeedbacks = feedbacks.filter(
    (f) =>
      f.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      f.user_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      f.ailyce_feedback?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      f.suggestions?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Paginate
  const paginatedFeedbacks = filteredFeedbacks.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ px: 6, pt: 3, pb: 4, width: '100%', bgcolor: '#f5f6f7', height: 'calc(100vh - 90px)', display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
      {/* Header */}
      <PageTitleContainer
        title="Feedback management"
        rightContent={
          <Button
            variant="contained"
            startIcon={exporting ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
            onClick={handleExportToExcel}
            disabled={exporting || feedbacks.length === 0}
            sx={{
              bgcolor: '#485869',
              color: 'white',
              py: 1.5,
              px: 3,
              fontWeight: 500,
              '&:hover': { bgcolor: '#3A4754' },
              boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
              textTransform: 'none !important'
            }}
          >
            {exporting ? 'Exporting...' : 'Export to Excel'}
          </Button>
        }
        sx={{
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
          ml: 0,
          mr: 0,
          mb: 2,
          mt: 0,
          py: '16px'
        }}
        titleProps={{
          sx: { fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif", fontWeight: 700 }
        }}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {exportResult && (
        <Alert
          severity={exportResult.success ? 'success' : 'error'}
          sx={{ mb: 3 }}
          onClose={() => setExportResult(null)}
        >
          {exportResult.message}
        </Alert>
      )}

      <Box sx={{
        bgcolor: 'white',
        borderRadius: '4px',
        boxShadow: '0px 4px 20px rgba(0,0,0,0.05)',
        p: 5,
        flex: 1,
      }}>
      {/* Statistics Cards */}
      {stats && (
        <Grid container spacing={4}  sx={{ mb: 3, }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Submissions"
              value={stats.total_submissions}
              color="primary"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Avg Ailyce Rating"
              value={stats.avg_ailyce_rating?.toFixed(1) || '0'}
              subtitle={
                <Rating
                  value={stats.avg_ailyce_rating || 0}
                  readOnly
                  size="small"
                  precision={0.1}
                />
              }
              color="info"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Enthusiastic Users"
              value={`${stats.enthusiastic_percentage || 0}%`}
              subtitle={`${stats.enthusiastic_count || 0} users`}
              color="success"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Need Training"
              value={`${stats.needs_training_percentage || 0}%`}
              subtitle={`${stats.needs_training_count || 0} users`}
              color="warning"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Avg Help Rating"
              value={stats.avg_help_rating?.toFixed(1) || '0'}
              subtitle={
                <Rating
                  value={stats.avg_help_rating || 0}
                  readOnly
                  size="small"
                  precision={0.1}
                />
              }
              color="info"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Avg Food Rating"
              value={stats.avg_food_rating?.toFixed(1) || '0'}
              subtitle={
                <Rating
                  value={stats.avg_food_rating || 0}
                  readOnly
                  size="small"
                  precision={0.1}
                />
              }
              color="info"
            />
          </Grid>
        </Grid>
      )}

      {/* Search */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'flex-end' }}>
        <TextField
          placeholder="Search users"
          value={searchTerm}
          onChange={handleSearchChange}
          variant="outlined"
          sx={{
            width: '30%',
            '& .MuiOutlinedInput-root': {
              borderRadius: '4px',
              height: '45px',
              transition: 'all 0.2s',
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.main',
                borderWidth: '1px',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.main',
                borderWidth: '1px',
              },
            },
            '& .MuiInputBase-input': {
              fontSize: '16px',
              padding: '0 20px',
              height: '45px',
              boxSizing: 'border-box'
            },
            '& .MuiInputBase-input::placeholder': {
              fontSize: '15px',
              opacity: 0.7
            },
          }}
        />
      </Box>

      {/* Feedback Table */}
      <Paper elevation={0} sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{
          maxHeight: 600,
          '&::-webkit-scrollbar': {
            width: '8px',
            backgroundColor: 'transparent',
          },
          '&::-webkit-scrollbar-track': {
            borderRadius: '10px',
            backgroundColor: 'rgba(0, 0, 0, 0.05)',
            marginTop: '5px',
            marginBottom: '5px',
          },
          '&::-webkit-scrollbar-thumb': {
            borderRadius: '10px',
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
            '&:hover': {
              backgroundColor: 'rgba(25, 118, 210, 0.4)',
            },
          },
          '&::-webkit-scrollbar-corner': {
            backgroundColor: 'transparent',
          },
        }}>
          <Table stickyHeader size="small" sx={{ tableLayout: 'fixed' }}>
            <TableHead sx={{ position: 'sticky', top: 0, zIndex: 10 }}>
              <TableRow>
                <TableCell sx={{ fontStyle: "normal !important", fontSize: "16px !important", fontWeight: "bold !important", width: '4%' }} />
                <TableCell sx={{ fontStyle: "normal !important", fontSize: "16px !important", fontWeight: "bold !important", width: '16%' }}>User Name</TableCell>
                <TableCell sx={{ fontStyle: "normal !important", fontSize: "16px !important", fontWeight: "bold !important", width: '14%' }}>Ailyce Rating</TableCell>
                <TableCell sx={{ fontStyle: "normal !important", fontSize: "16px !important", fontWeight: "bold !important", width: '12%' }}>Enthusiastic</TableCell>
                <TableCell sx={{ fontStyle: "normal !important", fontSize: "16px !important", fontWeight: "bold !important", width: '14%' }}>Needs Training</TableCell>
                <TableCell sx={{ fontStyle: "normal !important", fontSize: "16px !important", fontWeight: "bold !important", width: '14%' }}>Help Rating</TableCell>
                <TableCell sx={{ fontStyle: "normal !important", fontSize: "16px !important", fontWeight: "bold !important", width: '14%' }}>Food Rating</TableCell>
                <TableCell sx={{ fontStyle: "normal !important", fontSize: "16px !important", fontWeight: "bold !important" }}>Submitted At</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedFeedbacks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body1" color="text.secondary" sx={{ py: 4 }}>
                      {searchTerm
                        ? 'No feedback matches your search'
                        : 'No feedback submissions yet'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedFeedbacks.map((feedback) => (
                  <FeedbackRow
                    key={feedback.id}
                    feedback={feedback}
                    expanded={expandedRow === feedback.id}
                    onToggle={() => handleRowToggle(feedback.id)}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {/* Custom Pagination - only show when there are results */}
        {feedbacks.length > rowsPerPage && <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderTop: '1px solid rgba(0, 0, 0, 0.08)',
          minHeight: 52,
          px: 3,
        }}>
          {/* Left: displayed rows */}
          <Typography sx={{ fontSize: '14px', color: '#475569', fontWeight: 500 }}>
            {filteredFeedbacks.length === 0
              ? '0–0 of 0'
              : `${page * rowsPerPage + 1}–${Math.min((page + 1) * rowsPerPage, filteredFeedbacks.length)} of ${filteredFeedbacks.length}`}
          </Typography>

          {/* Center: pagination arrows */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              size="small"
              onClick={(e) => handleChangePage(e, page - 1)}
              disabled={page === 0}
              sx={{
                color: '#475569',
                '&:hover': { backgroundColor: 'rgba(71, 85, 105, 0.08)' },
                '&.Mui-disabled': { color: 'rgba(0, 0, 0, 0.2)' },
              }}
            >
              <ChevronLeftIcon />
            </IconButton>
            <IconButton
              size="small"
              onClick={(e) => handleChangePage(e, page + 1)}
              disabled={page >= Math.ceil(filteredFeedbacks.length / rowsPerPage) - 1}
              sx={{
                color: '#475569',
                '&:hover': { backgroundColor: 'rgba(71, 85, 105, 0.08)' },
                '&.Mui-disabled': { color: 'rgba(0, 0, 0, 0.2)' },
              }}
            >
              <ChevronRightIcon />
            </IconButton>
          </Box>

          {/* Right: rows per page */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography sx={{ fontSize: '14px', color: '#475569', fontWeight: 500 }}>
              Rows per page:
            </Typography>
            <Select
              value={rowsPerPage}
              onChange={(e) => {
                handleChangeRowsPerPage({ target: { value: e.target.value } });
              }}
              size="small"
              variant="standard"
              disableUnderline
              sx={{
                fontSize: '14px',
                color: '#475569',
                fontWeight: 500,
                '& .MuiSelect-select': {
                  paddingRight: '24px !important',
                  py: 0,
                },
              }}
            >
              {[5, 10, 25, 50].map((opt) => (
                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
              ))}
            </Select>
          </Box>
        </Box>}
      </Paper>
      </Box>
    </Box>
  );
};

export default FeedbackManagement;
